/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
import { DatePicker, Input } from 'antd';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { InvertFillButton } from '../InvertFillButton';
import './style.scss';
import callApi from '../../Api/config';
import { DOCUMENT_USER_ADDITIONAL_DETAILS } from '../../constants/EntityName';
import { toastError, toastSuccess } from '../../service/Toast';

const formatSavedDate = (value: any) => {
  if (!value || value === ' ') return '—';
  const m = moment(value);
  return m.isValid() ? m.format('YYYY-MM-DD') : '—';
};

const parseDateOfBirth = (value: any) => {
  if (!value || value === ' ') return null;
  const parsedDate = moment(value, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
  return parsedDate.isValid() ? parsedDate : null;
};

/**
 * Application Tracking details panel.
 *
 * IMPORTANT: This panel is for member-level application tracking only
 * (referenceNumber, dateOfBirth, passportNumber, trackingLink at the
 * top level of the member row). It must NOT read or write the
 * `finalEmailTrackingDetails` blob — that lives in its own panel and is
 * stored on `member.formData.finalEmailTrackingDetails`.
 */
const DocumitraAdditionalDetail = (props: any) => {
  // Persisted snapshot rendered next to the Update button. Always derived
  // from props so it stays in sync with the latest member data.
  const saved = {
    referenceNumber: props?.referenceNumber || '',
    dateOfBirth: props?.dateOfBirth || '',
    passportNumber: props?.passportNumber || '',
    trackingLink: props?.trackingLink || '',
  };
  const hasSaved =
    !!saved.referenceNumber ||
    (!!saved.dateOfBirth && saved.dateOfBirth !== ' ') ||
    !!saved.passportNumber ||
    !!saved.trackingLink;

  // Editable form state
  const [additionalDetail, setAdditionalDetail] = useState<any>({
    ...saved,
  });
  const [isEditing, setIsEditing] = useState(!hasSaved);

  // Re-sync when the parent supplies fresh member props (e.g. after another
  // panel updates the member object).
  useEffect(() => {
    setAdditionalDetail(saved);
    setIsEditing(!hasSaved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.referenceNumber, props?.dateOfBirth, props?.passportNumber, props?.trackingLink]);

  const handleOnchange = (e: any) => {
    const { name, value } = e.target;
    setAdditionalDetail((p: any) => ({ ...p, [name]: value }));
  };

  const handleOnchangeDate = (name: any, dateOfBirth: any) => {
    const formattedDate = dateOfBirth
      ? moment(dateOfBirth).startOf('day').format('YYYY-MM-DD')
      : null;
    setAdditionalDetail((prevState: any) => ({ ...prevState, [name]: formattedDate }));
  };

  const submitAdditionalDetail = async () => {
    // Whitelist exactly the application-tracking fields. Do NOT spread
    // arbitrary props (which historically caused finalEmailTrackingDetails to
    // be PATCHed back into the member via this panel).
    const payload = {
      referenceNumber: additionalDetail.referenceNumber,
      dateOfBirth: additionalDetail.dateOfBirth,
      passportNumber: additionalDetail.passportNumber,
      trackingLink: additionalDetail.trackingLink,
    };
    try {
      const res = await callApi(
        `${DOCUMENT_USER_ADDITIONAL_DETAILS}/${props?.memberId}`,
        'PATCH',
        payload
      );
      if (res?.data?.statusCode === 200) {
        toastSuccess(`User's additional details updated successfully.`);
        props?.onUpdate?.(payload);
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error:', error);
      toastError(`An error occurred while updating member's additional details.`);
    }
  };

  return (
    <div
      style={{
        width: '100%',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        background: '#fff',
        padding: '18px 20px',
        marginBottom: '18px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          fontSize: '16px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'black',
          marginBottom: '14px',
          paddingBottom: '10px',
          borderBottom: '1px solid #e2e8f0',
          lineHeight: 1.2,
        }}
      >
        <div>Additional Application Details</div>
        <InvertFillButton
          variant="blue"
          fontSize="base"
          fontWeight="medium"
          onClick={isEditing ? submitAdditionalDetail : () => setIsEditing(true)}
        >
          {isEditing ? 'Update' : 'Edit'}
        </InvertFillButton>
      </div>
      <div className="additional-container">
        <div className="additional-detail">
          <label className="additional-label">Reference Number:</label>
          {isEditing ? (
            <Input
              type="text"
              name="referenceNumber"
              value={additionalDetail?.referenceNumber}
              onChange={handleOnchange}
            />
          ) : (
            <div className="additional-value">{saved.referenceNumber || '—'}</div>
          )}
        </div>
        <div className="additional-detail">
          <label className="additional-label">Date of Birth:</label>
          {isEditing ? (
            <div
              onPaste={(event: any) => {
                const pastedValue = event.clipboardData?.getData('text')?.trim();
                const pastedDate = moment(pastedValue, ['DD/MM/YYYY', 'YYYY-MM-DD'], true);

                if (pastedDate.isValid()) {
                  event.preventDefault();
                  handleOnchangeDate('dateOfBirth', pastedDate);
                }
              }}
            >
              <DatePicker
                name="dateOfBirth"
                defaultPickerValue={moment()}
                value={parseDateOfBirth(additionalDetail.dateOfBirth)}
                format={['DD/MM/YYYY', 'YYYY-MM-DD']}
                placeholder="DD/MM/YYYY"
                onChange={dateOfBirth => handleOnchangeDate('dateOfBirth', dateOfBirth)}
              />
            </div>
          ) : (
            <div className="additional-value">{formatSavedDate(saved.dateOfBirth)}</div>
          )}
        </div>
        <div className="additional-detail">
          <label className="additional-label">Passport Number:</label>
          {isEditing ? (
            <Input
              type="text"
              name="passportNumber"
              value={additionalDetail?.passportNumber}
              onChange={handleOnchange}
            />
          ) : (
            <div className="additional-value">{saved.passportNumber || '—'}</div>
          )}
        </div>
        <div className="additional-detail">
          <label className="additional-label">Tracking Link:</label>
          {isEditing ? (
            <Input
              type="text"
              name="trackingLink"
              value={additionalDetail?.trackingLink}
              onChange={handleOnchange}
            />
          ) : (
            <div className="additional-value">{saved.trackingLink || '—'}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumitraAdditionalDetail;
